import * as THREE from 'three';

/** The source is explicit: intensity and Rhubarb shapes are not phoneme timestamps. */
export interface SpeechPerformanceFrame {
  utteranceId: number;
  seconds: number;
  state: 'playing' | 'paused' | 'waiting' | 'ended' | 'idle';
  source: 'authored-shapes' | 'character-timing' | 'amplitude' | 'none';
  activity: number;
}
export const QUIET_SPEECH_FRAME: SpeechPerformanceFrame = {
  utteranceId: 0, seconds: 0, state: 'idle', source: 'none', activity: 0,
};
export interface LanguageActingPose {
  headYaw: number;
  headPitch: number;
  brow: number;
  glanceX: number;
  glanceY: number;
  blink: number;
}
const clamp = (x: number, max = 1) => Number.isFinite(x) ? Math.max(0, Math.min(max, x)) : 0;
const smooth = (x: number) => { const t = clamp(x); return t * t * (3 - 2 * t); };
const pulse = (elapsed: number, duration: number) => elapsed < 0 || elapsed > duration ? 0
  : smooth(elapsed / (duration * .3)) * smooth((duration - elapsed) / (duration * .7));

/**
 * Phrase-level, bounded acting. Audio time schedules speech accents; wall time
 * only settles a stopped pose and schedules occasional quiet gaze shifts.
 * No phoneme inference, speech synthesis, timers, RAF or hand/body tracks.
 */
export function createLanguagePerformanceSampler(seed = .5) {
  let lastWall: number | null = null;
  let utteranceId = -1;
  let lastSeconds = -1;
  let wasPlaying = false;
  let quietSince: number | null = null;
  let accentAt = -Infinity;
  let accentIndex = 0;
  let previousActivity = 0;
  let glanceAt = -Infinity;
  let nextGlanceAt: number | null = null;
  let blinkAt = -Infinity;
  let random = Math.floor(clamp(seed) * 2147483646) + 1;
  let yaw = 0, pitch = 0;
  const rand = () => { random = (random * 16807) % 2147483647; return random / 2147483647; };
  let direction = seed < .5 ? -1 : 1;
  return {
    sample(frame: SpeechPerformanceFrame, now: number, listening = false): LanguageActingPose {
      if (!Number.isFinite(now)) return { headYaw: 0, headPitch: 0, brow: 0, glanceX: 0, glanceY: 0, blink: 0 };
      const dt = lastWall === null ? 0 : Math.min(.1, Math.max(0, now - lastWall));
      lastWall = now;
      const playing = frame.state === 'playing' && Number.isFinite(frame.seconds);
      const activity = clamp(frame.activity);
      // A new source/turn or seek invalidates every accent of the previous audio.
      if (utteranceId !== frame.utteranceId || (playing && frame.seconds < lastSeconds - .08)) {
        utteranceId = frame.utteranceId; lastSeconds = -1; quietSince = null;
        accentAt = -Infinity; accentIndex = 0; previousActivity = 0;
      }
      if (playing && frame.seconds !== lastSeconds) {
        if (activity < .06) {
          quietSince ??= frame.seconds;
        } else {
          const afterPause = quietSince !== null && frame.seconds - quietSince >= .3;
          const onset = !wasPlaying || accentIndex === 0;
          const emphasis = activity > .32 && activity - previousActivity > .12;
          if ((onset || afterPause || emphasis) && frame.seconds - accentAt >= 1.6) {
            accentAt = frame.seconds;
            accentIndex++;
            direction = accentIndex % 2 ? (seed < .5 ? -1 : 1) : (seed < .5 ? 1 : -1);
          }
          quietSince = null;
        }
        previousActivity = activity;
        lastSeconds = frame.seconds;
      }
      if (wasPlaying && !playing) { blinkAt = now + .15; accentAt = -Infinity; quietSince = null; }
      wasPlaying = playing;
      // A small phrase emphasis, never a continuous audio-volume head bob.
      const accent = playing ? pulse(frame.seconds - accentAt, .95) : 0;
      const quiet = playing && quietSince !== null && frame.seconds - quietSince >= .25;
      const targetYaw = playing && !quiet ? accent * direction * .8 : 0;
      const targetPitch = playing && !quiet ? accent * -.65 : 0;
      const alpha = 1 - Math.exp(-dt / .13);
      yaw += (targetYaw - yaw) * alpha;
      pitch += (targetPitch - pitch) * alpha;

      nextGlanceAt ??= now + 3.7 + rand() * 3;
      if (now >= nextGlanceAt) {
        glanceAt = now;
        nextGlanceAt = now + 5 + rand() * 5;
      }
      // Hold eye contact while listening; brief glances return to the user.
      const glance = listening ? 0 : pulse(now - glanceAt, .85);
      return {
        headYaw: yaw, headPitch: pitch,
        brow: quiet ? 0 : accent * .055,
        glanceX: glance * direction * .022,
        glanceY: glance * .008,
        blink: pulse(now - blinkAt, .19),
      };
    },
  };
}

/** Captured local rest pose; no accumulation, translation, scaling or hand writes. */
export function createLanguageHeadDriver(model: THREE.Object3D) {
  const head = model.getObjectByName('Head');
  const neck = model.getObjectByName('Neck');
  const headRest = head?.quaternion.clone();
  const neckRest = neck?.quaternion.clone();
  const delta = new THREE.Quaternion();
  const euler = new THREE.Euler(0, 0, 0, 'YXZ');
  return {
    update(pose: Pick<LanguageActingPose, 'headYaw' | 'headPitch'>) {
      const yaw = Math.max(-1, Math.min(1, Number.isFinite(pose.headYaw) ? pose.headYaw : 0));
      const pitch = Math.max(-1, Math.min(1, Number.isFinite(pose.headPitch) ? pose.headPitch : 0));
      euler.set(THREE.MathUtils.degToRad(pitch), THREE.MathUtils.degToRad(yaw), 0);
      delta.setFromEuler(euler);
      if (head && headRest) head.quaternion.copy(headRest).multiply(delta).normalize();
      if (neck && neckRest) neck.quaternion.copy(neckRest);
    },
    dispose() {
      if (head && headRest) head.quaternion.copy(headRest);
      if (neck && neckRest) neck.quaternion.copy(neckRest);
    },
  };
}

/** The same one-channel pose reaches head and teeth, never a second jaw layer. */
export function writeLanguageLiveMouth(
  targets: readonly { morphTargetDictionary: Record<string, number>; morphTargetInfluences: number[] }[],
  weights: Readonly<Record<string, number>>, names: readonly string[],
) {
  for (const target of targets) for (const name of names) {
    const index = target.morphTargetDictionary[name];
    if (index !== undefined) target.morphTargetInfluences[index] = clamp(weights[name] ?? 0);
  }
}
